import { ApiActionDescriptor, ApiDispatcherInterface } from "./types";

/**
 * An API error, carrying what the server said.
 *
 * A class rather than a bare Error so a caller can tell a refused request from a
 * network failure without matching on the message.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: string,
    readonly descriptor: ApiActionDescriptor
  ) {
    super(
      `${descriptor.method ?? "GET"} ${descriptor.path} answered ${status}` +
        (body ? `: ${body}` : "")
    );
    this.name = "ApiError";
  }
}

/*
 * Statuses defined to carry no body. `Response.json()` on one throws
 * "Unexpected end of JSON input", and the rejection took the rest of the caller's chain
 * with it: DELETE answers 204, so deleting an item removed it on the server and then
 * silently skipped both the refetch and the redirect. The list only caught up on reload.
 *
 * 304 is the third null-body status and is deliberately not here: it is not `ok`, so it
 * is refused above before this is reached. Listing it would suggest a path that does not
 * exist.
 */
const NULL_BODY_STATUSES = new Set([204, 205]);

export class ApiDispatcher implements ApiDispatcherInterface {
  dispatch = async <R>(action: ApiActionDescriptor): Promise<R> => {
    const resp = await fetch(action.path, {
      method: action.method ?? "GET",
      credentials: action.credentials ?? "same-origin",
      // oxlint-disable-next-line no-invalid-fetch-options
      body: action.body,
    });

    if (!resp.ok) {
      throw new ApiError(resp.status, await resp.text(), action);
    }

    if (NULL_BODY_STATUSES.has(resp.status)) {
      return undefined as R;
    }

    /*
     * Read as text and parse, rather than `resp.json()`. A 200 with an empty body is
     * not supposed to happen, but if one ever does this answers `undefined` instead of
     * throwing a parse error that reads like a bug in the caller.
     */
    const text = await resp.text();
    if (text === "") {
      return undefined as R;
    }
    return JSON.parse(text) as R;
  };
}
